import * as Sentry from "@sentry/browser"
import { useDocumentStore } from "@shared/components/providers/SupergrainProvider"
import { blankUnifiedLessonAttributes } from "@shared/lib/card-stack/blank-lesson"
import { cloneCardStackAttributesWithNewIds } from "@shared/lib/card-stack/clone"
import { emptyCardStackExtracted } from "@shared/lib/card-stack/extracted"
import { emptyProseMirrorContent } from "@shared/lib/card-stack/prose-mirror-utils"
import { isUnifiedCardStack } from "@shared/lib/card-stack/unified-migration"
import { stripPrototypesInPlace } from "@shared/lib/strip-prototypes"
import type { CardStack, ParentCardStack } from "@shared/models/card-stack"
import type { Permission } from "@shared/models/permissions"
import type { TypeToModel } from "@shared/models/type-to-model"
import ObjectId from "@shared/util/object-id-gen"
import { batch } from "@supergrain/kernel"
import type { DocumentStore } from "@supergrain/silo"
import type SocketService from "cc-frontend/services/socket"
import { useEmberService } from "react-app/providers/ember-service-context"

/**
 * Phantom lessons: the client-invented card stack that makes an empty planner day editable. A
 * phantom's claim on its day lives on the document itself (`meta.phantomFor`), honored only while
 * `meta.isNewLesson` is true — `LESSON_FIRST_EDIT` clears that on promotion. No registry, no
 * invalidation: an id never exists without a document behind it.
 */
export interface PhantomLessonDeps {
  documentStore: DocumentStore<TypeToModel>
  /** Subscribes the socket to a document. Idempotent. */
  subscribeToDocument: (type: string, id: string) => void
}

export function usePhantomLessonDeps(): PhantomLessonDeps {
  const documentStore = useDocumentStore()
  const socket = useEmberService<SocketService>("socket")
  return { documentStore, subscribeToDocument: (type, id) => socket.subscribe(type, id) }
}

/**
 * For each store, maps `ownerId:dateString` to the cached phantom CardStack's id.
 */
const phantomLessonIds = new WeakMap<DocumentStore<TypeToModel>, Map<string, string>>()
function cacheKey(ownerId: string, dateString: string) {
  return `${ownerId}:${dateString}`
}

/**
 * Finds an existing phantom lesson for the given slot, if it is still a valid phantom.
 */
export function findPhantomLesson(
  documentStore: DocumentStore<TypeToModel>,
  courseId: string,
  dateString: string,
): CardStack | null {
  const key = cacheKey(courseId, dateString)
  const ids = phantomLessonIds.get(documentStore)
  if (!ids) return null
  const id = ids.get(key)
  if (!id) return null

  // Check that the cached id is still valid: it corresponds to a document
  // that is still a phantom (not persisted by LESSON_FIRST_EDIT)
  // and is for this slot.
  const doc = documentStore.findInMemory("card-stack", id)
  if (doc) {
    const claim = doc.meta?.phantomFor
    if (
      doc.meta?.isNewLesson === true &&
      claim?.ownerId === courseId &&
      claim.dateString === dateString
    ) {
      return doc
    }
  }

  // The cached id was invalid.
  ids.delete(key)
  return null
}

/**
 * Mints the date's phantom, deliberately unfilled: identity exists as soon as anyone asks, and
 * content lands separately through {@link applyTemplateToLessonIfNeeded}. Idempotent.
 */
export function ensurePhantomLesson(
  deps: PhantomLessonDeps,
  {
    courseId,
    planbookId,
    dateString,
    permissions,
  }: {
    courseId: string
    planbookId: string
    dateString: string
    permissions: Array<Permission>
  },
): string {
  const existing = findPhantomLesson(deps.documentStore, courseId, dateString)
  if (existing) return existing.id

  const id = ObjectId.create()
  const phantom: CardStack = {
    id,
    type: "card-stack",
    attributes: blankUnifiedLessonAttributes(),
    relationships: {
      course: { data: { type: "course", id: courseId } },
      planbook: { data: { type: "planbook", id: planbookId } },
    },
    meta: {
      isNewLesson: true,
      permissions,
      cardsUpdatingFromTemplate: true,
      phantomFor: { ownerId: courseId, dateString },
    },
  }
  deps.documentStore.insertDocument("card-stack", phantom)
  let ids = phantomLessonIds.get(deps.documentStore)
  if (!ids) phantomLessonIds.set(deps.documentStore, (ids = new Map()))
  ids.set(cacheKey(courseId, dateString), id)
  // Once promoted, this tab must hear the server's patches for the id. Idempotent.
  deps.subscribeToDocument("card-stack", id)
  return id
}

/**
 * Fills an unfilled lesson from its template — or blank when `template` is null (no template, or
 * it failed to load) — and re-applies when the template itself changes. Neither fetches nor awaits.
 */
export function applyTemplateToLessonIfNeeded(
  deps: PhantomLessonDeps,
  cardStackId: string,
  templateId: string | null,
  template: CardStack | null,
): void {
  const lesson = deps.documentStore.findInMemory("card-stack", cardStackId)
  if (!lesson) return
  if (!needsTemplateCards(lesson, templateId, template)) return

  if (templateId !== null && template === null) {
    Sentry.withScope((scope) => {
      scope.setLevel(Sentry.Severity.Error)
      scope.setExtra("CardStackTemplateId", templateId)
      scope.setExtra("cardStackId", cardStackId)
      Sentry.captureMessage("CardStackTemplate is undefined.")
    })
  }

  const attributes =
    template === null
      ? {
          cards: [],
          proseMirror: emptyProseMirrorContent(),
          extracted: emptyCardStackExtracted(),
        }
      : clonedTemplateAttributes(deps, cardStackId, template, lesson.attributes.title || "")

  batch(() => {
    for (const [key, value] of Object.entries(attributes)) {
      // This writes into the stored Supergrain document, bypassing the insert hook that normally
      // enforces the prototype-free invariant (see @shared/lib/strip-prototypes) — so strip here.
      // In place is safe: every value was freshly built above and nothing else holds a reference.
      stripPrototypesInPlace(value)
      lesson.attributes[key] = value
    }
    // The store's insert hook fills meta in, but a document can reach here without it.
    const meta = (lesson.meta ??= { permissions: [] })
    meta.appliedTemplate = template === null ? undefined : appliedTemplateStamp(template)
    meta.cardsUpdatingFromTemplate = true
  })
}

/**
 * A lesson wants the template while it is flagged as updating, or while it has no content yet —
 * except once it already carries that exact revision of it.
 *
 * That last check is what keeps this callable from a render body. `cardsUpdatingFromTemplate`
 * stays true until the lesson's first edit persists it, so without it every render would re-clone
 * the template and write the document it just read.
 */
function needsTemplateCards(
  lesson: TypeToModel["card-stack"],
  templateId: string | null,
  template: CardStack | null,
): boolean {
  if (template === null) {
    // Nothing to copy, so the blank fallback is worth writing exactly once — otherwise a template
    // that failed to load would rewrite the day, and re-report itself, on every render.
    if (lesson.attributes.cards !== null && lesson.attributes.proseMirror !== null) return false
  } else if (lesson.meta?.appliedTemplate === appliedTemplateStamp(template)) {
    return false
  }

  if (lesson.meta?.cardsUpdatingFromTemplate === true) return true
  if (templateId === null) return false
  // Cards are checked even on a unified lesson, in case it migrated but was never edited.
  if (lesson.attributes.cards !== null) return false
  return !(isUnifiedCardStack(lesson) && lesson.attributes.proseMirror !== null)
}

/**
 * Which revision of which template a lesson is carrying, recorded on the lesson so the next
 * render can tell it is already up to date.
 *
 * `_revision`, not `attributes.version`: version is bumped by a single unrelated action and does
 * not move when a template's content is edited, so a guard on it would read "already applied"
 * forever and an edited template would never reach the day.
 */
function appliedTemplateStamp(template: CardStack): string {
  const revision = template.attributes._revision
  return `${template.id}:${typeof revision === "number" ? revision : 0}`
}

/**
 * template card id -> the id that card already has on this lesson, read straight off the
 * document: `cloneCardStackAttributesWithNewIds` stamps every cloned card with its source id as
 * `parentCardId`. Fresh ids on a re-application would tear down and rebuild every ProseMirror
 * node view.
 */
function templateCardIdsFromLesson(lesson: CardStack | undefined): Map<string, string> {
  const ids = new Map<string, string>()
  const json = lesson?.attributes.proseMirror?.json as
    | { content?: Array<{ attrs?: { id?: unknown; parentCardId?: unknown } }> }
    | undefined
  if (!json?.content) return ids

  for (const node of json.content) {
    const attrs = node?.attrs
    if (typeof attrs?.parentCardId === "string" && typeof attrs.id === "string") {
      ids.set(attrs.parentCardId, attrs.id)
    }
  }
  return ids
}

/** The template's content, cloned onto this lesson with ids that survive re-application. */
function clonedTemplateAttributes(
  deps: PhantomLessonDeps,
  cardStackId: string,
  template: CardStack,
  overrideTitle: string,
) {
  const parentCardStack: ParentCardStack = {
    id: template.id,
    version: template.attributes.version,
    ownerIds: null,
    courseId: null,
    planbookId: null,
    source: "template",
  }

  const existingIds = templateCardIdsFromLesson(
    deps.documentStore.findInMemory("card-stack", cardStackId),
  )

  const cloned = cloneCardStackAttributesWithNewIds(template.attributes, parentCardStack, true, {
    overrideTitle,
    mapCardId: (templateCardId) => existingIds.get(templateCardId) ?? ObjectId.create(),
    onMigrationError: (error) => Sentry.captureException(error),
  })

  // Cloning as unified yields unified attributes; the return type spans both schemas.
  if (cloned.schemaVersion !== 2)
    throw Error("Cloned a template as unified but got legacy attributes back")

  return {
    cards: cloned.cards,
    proseMirror: cloned.proseMirror,
    extracted: cloned.extracted,
    parentCardStack: cloned.parentCardStack,
  }
}
